import { Check, ChevronsUpDown, Layers, Lock } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { setActiveModule } from "@/lib/module-context";
import type { ModuleKey } from "@/lib/module-registry";
import { moduleMenuEntries, moduleOrDefault } from "@/lib/modules";
import { useAuthSession } from "@/lib/session-context";
import { cn } from "@/lib/utils";

/**
 * The sidebar's app-picker control — a real switcher, not just a styled
 * label. Clicking it opens a popover listing every module in the registry
 * (current one checked, unbuilt ones locked), so switching modules is one
 * click from anywhere in the product, not only from the dashboard-first
 * home's own searchable module picker (`ModulesLauncherButton`). This is what
 * makes the current app read as an "app" rather than a page title: it
 * behaves like the module switcher every multi-product console (Vercel's
 * team/project switcher, Linear's workspace switcher, Slack's workspace
 * header) uses for the same job.
 *
 * Renders below the full NorthStar wordmark (`SidebarFullLogo`, in each
 * sidebar's header) rather than carrying its own brand mark — the two used
 * to be combined in one row, but that squeezed a long app name (e.g.
 * "Market Intelligence") against the logomark and the chevron. Splitting
 * them into two stacked rows gives the module name the sidebar's full width.
 *
 * `activeKey` identifies which row gets the checkmark. It's any module key from
 * the registry (`lib/modules`) — no longer a hardcoded union, so a new module needs
 * no edit here. Compact (icons-only) sidebar mode doesn't render this at all
 * — the plain brand mark stays the "go home" affordance there (see
 * `SidebarBrandLink`), since a dropdown trigger needs room for a label to
 * make sense as a control.
 */
export function ModuleSwitcher({ activeKey }: { activeKey: string }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const active = moduleOrDefault(activeKey);
  const ActiveIcon = active.icon;

  // Every module A–Z, built and unbuilt interleaved — see `moduleMenuEntries`.
  // Modules with no `href` have no routes yet and render disabled with a lock.
  // Flags decide whether a lockable module (Market Intelligence) offers a
  // link or renders with a padlock. Safe here: `ModuleSwitcher` only mounts
  // inside `AppShell`, where a session exists.
  const session = useAuthSession();
  const entries = moduleMenuEntries(session.features, session.permissions, {
    isSuperAdmin: session.is_super_admin,
    isSupplier: session.is_supplier,
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Switch app — currently ${active.label}`}
          className={cn(
            "group flex w-full min-w-0 items-center gap-2 rounded-lg border border-border bg-surface-elevated px-2.5 py-2 text-left transition-colors",
            "hover:border-border-strong hover:bg-row-hover",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            "data-[state=open]:border-border-strong data-[state=open]:bg-row-hover",
          )}
        >
          <span className="flex min-w-0 flex-1 items-center justify-center gap-1.5">
            <ActiveIcon size={16} strokeWidth={1.75} className="shrink-0 text-muted-foreground" />
            <span className="text-center text-base font-semibold leading-tight text-foreground">
              {active.label}
            </span>
          </span>
          <ChevronsUpDown
            size={14}
            strokeWidth={1.75}
            className="shrink-0 self-center text-text-dim transition-transform group-hover:text-foreground"
          />
        </button>
      </PopoverTrigger>

      <PopoverContent align="start" sideOffset={8} className="w-64 p-1.5">
        <div className="px-2 py-1.5 text-xs font-bold uppercase tracking-eyebrow text-text-dim">
          Modules
        </div>
        {entries.map((mod) => (
          <AppSwitcherRow
            key={mod.key}
            label={mod.label}
            icon={mod.icon}
            href={mod.href}
            active={activeKey === mod.key}
            onSelect={() => {
              // Choosing a module makes it the active one even when its
              // landing is a shared record page (Utilities for a user who
              // cannot open Operations' Tasks), which would otherwise keep
              // the module they came from.
              if (mod.href) setActiveModule(mod.key as ModuleKey);
              setOpen(false);
            }}
          />
        ))}

        <div className="-mx-1 my-1 h-px bg-border" />

        <Link
          to="/dashboard"
          onClick={() => setOpen(false)}
          state={{ from: location.pathname }}
          className={cn(
            "flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-muted-foreground no-underline transition-colors",
            "hover:bg-row-hover hover:text-foreground",
          )}
        >
          <Layers size={14} strokeWidth={1.75} />
          Back to dashboard
        </Link>
      </PopoverContent>
    </Popover>
  );
}

function AppSwitcherRow({
  label,
  icon: Icon,
  href,
  active,
  onSelect,
}: {
  label: string;
  icon: typeof Layers;
  href?: string;
  active: boolean;
  onSelect: () => void;
}) {
  const rowClass = cn(
    "flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors",
    active ? "text-foreground" : "text-muted-foreground",
  );

  if (!href) {
    return (
      <div
        className={cn(rowClass, "cursor-not-allowed opacity-50")}
        aria-disabled="true"
      >
        <Icon size={14} strokeWidth={1.75} />
        <span className="flex-1">{label}</span>
        <Lock size={12} strokeWidth={1.75} className="text-text-dim" />
      </div>
    );
  }

  return (
    <Link
      to={href}
      onClick={onSelect}
      className={cn(rowClass, "no-underline hover:bg-row-hover hover:text-foreground")}
    >
      <Icon size={14} strokeWidth={1.75} />
      <span className="flex-1">{label}</span>
      {active && <Check size={13} strokeWidth={2} className="text-primary" />}
    </Link>
  );
}
